import React, { useState, useEffect, useCallback } from "react";
import Layout from "../components/Layout";
import config from "../config";
import "./ElementorBuilder.css";

const ElementorBuilder = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [builds, setBuilds] = useState([]);
  const [isLoadingBuilds, setIsLoadingBuilds] = useState(false);

  // Form fields
  const [formData, setFormData] = useState({
    figma_url: "",
    website_url: "",
    page_slug: "",
    qa_runs: 3,
    additional_notes: "",
    additional_figma_urls: [],
  });

  // Additional Figma URL input fields
  const [additionalUrlDescription, setAdditionalUrlDescription] = useState("");
  const [additionalUrlValue, setAdditionalUrlValue] = useState("");

  // Fetch builds for the current user
  const fetchBuilds = useCallback(async () => {
    try {
      setIsLoadingBuilds(true);
      const response = await fetch(`${config.api.baseUrl}/wordpress/builds`, {
        credentials: "include",
      });

      const data = await response.json();

      if (data.success) {
        setBuilds(data.data);
      } else {
        console.error("Failed to fetch builds:", data);
      }
    } catch (error) {
      console.error("Error fetching builds:", error);
    } finally {
      setIsLoadingBuilds(false);
    }
  }, []);

  // Fetch builds on mount and set up polling
  useEffect(() => {
    fetchBuilds();

    // Poll for updates every 5 seconds
    const interval = setInterval(fetchBuilds, 5000);

    return () => clearInterval(interval);
  }, [fetchBuilds]);

  // Handle input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Handle adding additional Figma URL
  const handleAddAdditionalUrl = () => {
    if (additionalUrlDescription.trim() && additionalUrlValue.trim()) {
      setFormData((prev) => ({
        ...prev,
        additional_figma_urls: [
          ...prev.additional_figma_urls,
          {
            description: additionalUrlDescription.trim(),
            url: additionalUrlValue.trim(),
          },
        ],
      }));
      setAdditionalUrlDescription("");
      setAdditionalUrlValue("");
    }
  };

  // Handle removing additional Figma URL
  const handleRemoveAdditionalUrl = (index) => {
    setFormData((prev) => ({
      ...prev,
      additional_figma_urls: prev.additional_figma_urls.filter(
        (_, i) => i !== index
      ),
    }));
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setErrorMessage("");
    setSuccessMessage("");

    try {
      // Validate required fields
      if (!formData.figma_url) {
        setErrorMessage("Figma URL is required");
        setIsLoading(false);
        return;
      }
      if (!formData.website_url) {
        setErrorMessage("Website URL is required");
        setIsLoading(false);
        return;
      }
      if (!formData.page_slug) {
        setErrorMessage("Page slug is required");
        setIsLoading(false);
        return;
      }

      // Submit to API (user email extracted from JWT token on backend)
      const response = await fetch(
        `${config.api.baseUrl}/wordpress/start-build`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          credentials: "include",
          body: JSON.stringify(formData),
        }
      );

      const data = await response.json();

      if (response.ok && data.status === "started") {
        setSuccessMessage(
          `Build process started successfully! The page will be built at ${data.website_url}${data.page_slug}.`
        );
        // Reset form
        setFormData({
          figma_url: "",
          website_url: "",
          page_slug: "",
          qa_runs: 3,
          additional_notes: "",
          additional_figma_urls: [],
        });
        // Refresh builds list
        fetchBuilds();
      } else {
        setErrorMessage(
          data.description || data.error || "Failed to start build process"
        );
      }
    } catch (error) {
      console.error("Error starting build:", error);
      setErrorMessage(
        "An error occurred while starting the build process. Please try again."
      );
    } finally {
      setIsLoading(false);
    }
  };

  // Prevent form submission on Enter key in text inputs
  const handleKeyDown = (e) => {
    if (e.key === "Enter" && e.target.tagName !== "TEXTAREA") {
      e.preventDefault();
    }
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  // Get status badge class
  const getStatusClass = (status) => {
    const statusMap = {
      initializing: "status-initializing",
      extracting_sections: "status-extracting",
      building: "status-building",
      completed: "status-completed",
      failed: "status-failed",
    };
    return statusMap[status] || "status-unknown";
  };

  // Get status display text
  const getStatusText = (status) => {
    const textMap = {
      initializing: "Initializing",
      extracting_sections: "Extracting Sections",
      building: "Building",
      completed: "Completed",
      failed: "Failed",
    };
    return textMap[status] || status;
  };

  return (
    <Layout>
      <div className="elementor-builder-page">
        <div className="elementor-builder-two-column">
          {/* Left Column - Form */}
          <div className="elementor-builder-form-column">
            <div className="elementor-builder-container">
              <h1 className="elementor-builder-title">
                Elementor Page Builder
              </h1>
              <p className="elementor-builder-subtitle">
                Build WordPress Elementor pages from Figma designs using AI
              </p>

              {errorMessage && (
                <div className="error-message">{errorMessage}</div>
              )}
              {successMessage && (
                <div className="success-message">{successMessage}</div>
              )}

              <form
                onSubmit={handleSubmit}
                className="elementor-builder-form"
                onKeyDown={handleKeyDown}
              >
                {/* Required Fields Section */}
                <div className="form-section">
                  <h2>Required Information</h2>

                  <div className="form-group">
                    <label htmlFor="figma_url">Figma URL *</label>
                    <input
                      type="url"
                      id="figma_url"
                      name="figma_url"
                      value={formData.figma_url}
                      onChange={handleInputChange}
                      required
                      placeholder="https://www.figma.com/design/..."
                    />
                    <span className="field-hint">
                      The main Figma design URL for the page
                    </span>
                  </div>

                  <div className="form-group">
                    <label htmlFor="website_url">Website URL *</label>
                    <input
                      type="url"
                      id="website_url"
                      name="website_url"
                      value={formData.website_url}
                      onChange={handleInputChange}
                      required
                      placeholder="https://example.wpengine.com"
                    />
                    <span className="field-hint">
                      The WordPress website URL where the page will be built
                    </span>
                  </div>

                  <div className="form-group">
                    <label htmlFor="page_slug">Page Slug *</label>
                    <input
                      type="text"
                      id="page_slug"
                      name="page_slug"
                      value={formData.page_slug}
                      onChange={handleInputChange}
                      required
                      placeholder="/landing-page"
                    />
                    <span className="field-hint">
                      The URL slug for the page (e.g., /landing-page)
                    </span>
                  </div>
                </div>

                {/* Optional Fields Section */}
                <div className="form-section">
                  <h2>Optional Settings</h2>

                  <div className="form-group">
                    <label htmlFor="qa_runs">QA Iterations per Section</label>
                    <input
                      type="number"
                      id="qa_runs"
                      name="qa_runs"
                      min="1"
                      max="10"
                      value={formData.qa_runs}
                      onChange={handleInputChange}
                      placeholder="3"
                    />
                    <span className="field-hint">
                      Maximum number of QA iterations per section before moving
                      on (default: 3)
                    </span>
                  </div>

                  <div className="form-group">
                    <label htmlFor="additional_notes">Additional Notes</label>
                    <textarea
                      id="additional_notes"
                      name="additional_notes"
                      value={formData.additional_notes}
                      onChange={handleInputChange}
                      placeholder="Any special instructions or notes about the design..."
                      rows="4"
                    />
                    <span className="field-hint">
                      Any additional context or special instructions for the
                      builder
                    </span>
                  </div>

                  {/* Additional Figma URLs */}
                  <div className="form-group">
                    <label>Additional Figma URLs</label>
                    <div className="additional-urls-input">
                      <input
                        type="text"
                        value={additionalUrlDescription}
                        onChange={(e) =>
                          setAdditionalUrlDescription(e.target.value)
                        }
                        placeholder="Description (e.g., Mobile, Tablet)"
                        className="url-description-input"
                      />
                      <input
                        type="url"
                        value={additionalUrlValue}
                        onChange={(e) => setAdditionalUrlValue(e.target.value)}
                        placeholder="https://www.figma.com/..."
                        className="url-value-input"
                      />
                      <button
                        type="button"
                        onClick={handleAddAdditionalUrl}
                        className="add-url-button"
                        disabled={
                          !additionalUrlDescription.trim() ||
                          !additionalUrlValue.trim()
                        }
                      >
                        Add
                      </button>
                    </div>
                    <span className="field-hint">
                      Additional Figma URLs for mobile, tablet, or other
                      variants
                    </span>

                    {formData.additional_figma_urls.length > 0 && (
                      <div className="additional-urls-list">
                        {formData.additional_figma_urls.map((item, index) => (
                          <div key={index} className="url-item">
                            <div className="url-item-content">
                              <strong>{item.description}:</strong>
                              <span className="url-text">{item.url}</span>
                            </div>
                            <button
                              type="button"
                              className="remove-url-button"
                              onClick={() => handleRemoveAdditionalUrl(index)}
                            >
                              ×
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                {/* Form Actions */}
                <div className="form-actions">
                  <button
                    type="submit"
                    className="submit-button"
                    disabled={isLoading}
                  >
                    {isLoading ? "Starting Build..." : "Start Build"}
                  </button>
                </div>
              </form>
            </div>
          </div>

          {/* Right Column - Builds List */}
          <div className="elementor-builder-builds-column">
            <div className="builds-container">
              <div className="builds-header">
                <h2>Your Builds</h2>
                {isLoadingBuilds && <span className="loading-spinner"></span>}
              </div>

              {builds.length === 0 && !isLoadingBuilds && (
                <div className="no-builds-message">
                  No builds yet. Start your first build!
                </div>
              )}

              <div className="builds-list">
                {builds.map((build) => (
                  <div key={build._id} className="build-card">
                    <div className="build-card-header">
                      <span
                        className={`build-status ${getStatusClass(
                          build.status
                        )}`}
                      >
                        {getStatusText(build.status)}
                      </span>
                      <span className="build-date">
                        {formatDate(build.created_at)}
                      </span>
                    </div>

                    <div className="build-card-body">
                      <div className="build-info-row">
                        <strong>Page:</strong>
                        <span className="build-page-slug">
                          {build.page_slug}
                        </span>
                      </div>

                      <div className="build-info-row">
                        <strong>Website:</strong>
                        <a
                          href={build.website_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="build-website-link"
                        >
                          {build.website_url}
                        </a>
                      </div>

                      {build.total_sections && (
                        <div className="build-info-row">
                          <strong>Sections:</strong>
                          <span>
                            {build.current_section_index !== null
                              ? `${build.current_section_index + 1} of ${
                                  build.total_sections
                                }`
                              : `${build.total_sections} total`}
                          </span>
                        </div>
                      )}

                      {build.current_section_name && (
                        <div className="build-info-row">
                          <strong>Current Section:</strong>
                          <span>{build.current_section_name}</span>
                        </div>
                      )}

                      {build.current_qa_iteration !== null &&
                        build.current_qa_iteration !== undefined && (
                          <div className="build-info-row">
                            <strong>QA Iteration:</strong>
                            <span>
                              {build.current_qa_iteration} of {build.qa_runs}
                            </span>
                          </div>
                        )}

                      <div className="build-progress-message">
                        {build.progress_message}
                      </div>

                      {build.error && (
                        <div className="build-error-message">
                          <strong>Error:</strong> {build.error}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default ElementorBuilder;
